import { useState, useMemo, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, AlertTriangle, CheckCircle, 
  Target, PieChart, BarChart3, Calculator, Users, Zap, ArrowUpRight, ArrowDownRight,
  FileText, Brain, Shield, Activity, Clock, Gauge, TrendingUp as Growth,
  Calendar, AlertCircle, ThumbsUp, ThumbsDown, Minus, ChevronRight, Info, Filter,
  Package, UserCog, Award, BookOpen, AlertOctagon
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, LineChart, Line, ComposedChart, Legend, RadialBarChart, RadialBar,
  PieChart as RechartsPie, Pie, Cell
} from 'recharts';
import { useApp } from '../context/AppContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#C41E3A', '#10B981', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899'];

// Currency conversion rate (approximate)
const USD_TO_MYR = 4.47;

// Period filter options
const PERIOD_OPTIONS = [
  { id: 'all', label: 'Semua', days: null },
  { id: 'week', label: 'Minggu Ini', days: 7 },
  { id: 'biweekly', label: '2 Minggu', days: 14 },
  { id: 'month', label: 'Bulan Ini', days: 30 },
  { id: 'quarter', label: 'Suku Tahun', days: 90 },
  { id: 'year', label: 'Tahun Ini', days: 365 },
];

// Helper to get date range
const getDateRange = (periodId) => {
  const now = new Date();
  const today = now.toISOString().split('T')[0];
  
  if (periodId === 'all') return { start: null, end: today };
  
  const option = PERIOD_OPTIONS.find(p => p.id === periodId);
  if (!option || !option.days) return { start: null, end: today };
  
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() - option.days);
  
  return { 
    start: startDate.toISOString().split('T')[0], 
    end: today 
  };
};

// Filter data by date range
const filterByDateRange = (data, dateRange) => {
  if (!dateRange.start) return data;
  return data.filter(item => {
    const itemDate = item.date;
    if (!itemDate) return true;
    return itemDate >= dateRange.start && itemDate <= dateRange.end;
  });
};

// Period Selector Component
const PeriodSelector = ({ selected, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {PERIOD_OPTIONS.map(option => (
      <button
        key={option.id}
        onClick={() => onChange(option.id)}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
          selected === option.id
            ? 'bg-red-700 text-white shadow-md'
            : 'bg-white text-gray-600 border border-gray-200 hover:border-red-300 hover:text-red-700'
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const formatCurrency = (value, currency = 'RM', showSign = false) => {
  if (value === null || value === undefined || isNaN(value)) return `${currency}0.00`;
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${currency}${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatCompact = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toFixed(0);
};

const formatPercent = (value, showSign = true) => {
  if (value === null || value === undefined || isNaN(value)) return '0.0%';
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

// Health Score Calculator
const calculateHealthScore = (metrics) => {
  let score = 100;
  
  // Profit Margin (max -30 points)
  if (metrics.profitMargin < 0) score -= 30;
  else if (metrics.profitMargin < 10) score -= 20;
  else if (metrics.profitMargin < 20) score -= 10;
  
  // Cash Runway (max -25 points)
  if (metrics.cashRunway < 2) score -= 25;
  else if (metrics.cashRunway < 4) score -= 15;
  else if (metrics.cashRunway < 6) score -= 5;
  
  // Expense Ratio (max -20 points)
  if (metrics.expenseRatio > 90) score -= 20;
  else if (metrics.expenseRatio > 75) score -= 10;
  else if (metrics.expenseRatio > 60) score -= 5;
  
  // Revenue Growth (max -15 points)
  if (metrics.revenueGrowth < -20) score -= 15;
  else if (metrics.revenueGrowth < -10) score -= 10;
  else if (metrics.revenueGrowth < 0) score -= 5;
  
  // Margin of Safety (max -10 points)
  if (metrics.marginOfSafety < 0) score -= 10;
  else if (metrics.marginOfSafety < 15) score -= 5;
  
  return Math.max(0, Math.min(100, score));
};

const getHealthGrade = (score) => {
  if (score >= 90) return { grade: 'A+', color: '#10B981', label: 'Cemerlang', bg: 'bg-emerald-500' };
  if (score >= 80) return { grade: 'A', color: '#10B981', label: 'Sangat Baik', bg: 'bg-emerald-500' };
  if (score >= 70) return { grade: 'B', color: '#3B82F6', label: 'Baik', bg: 'bg-blue-500' };
  if (score >= 60) return { grade: 'C', color: '#F59E0B', label: 'Sederhana', bg: 'bg-amber-500' };
  if (score >= 50) return { grade: 'D', color: '#F97316', label: 'Perlu Perhatian', bg: 'bg-orange-500' };
  return { grade: 'F', color: '#EF4444', label: 'Kritikal', bg: 'bg-red-500' };
};

// Gauge Component for Ratios
const GaugeChart = ({ value, max, label, thresholds, unit = '%' }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const getColor = () => {
    if (thresholds.good && value >= thresholds.good) return '#10B981';
    if (thresholds.warning && value >= thresholds.warning) return '#F59E0B';
    return '#EF4444';
  };
  
  const data = [{ value: percentage, fill: getColor() }];
  
  return (
    <div className="text-center">
      <ResponsiveContainer width="100%" height={120}>
        <RadialBarChart cx="50%" cy="80%" innerRadius="60%" outerRadius="100%" startAngle={180} endAngle={0} data={data}>
          <RadialBar background dataKey="value" cornerRadius={10} />
        </RadialBarChart>
      </ResponsiveContainer>
      <p className="text-2xl font-bold text-gray-800 -mt-8">{value.toFixed(1)}{unit}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
};

// Score Ring Component
const ScoreRing = ({ score, size = 120 }) => {
  const { grade, color, label, bg } = getHealthGrade(score);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={45} stroke="#e5e7eb" strokeWidth="10" fill="none" />
        <circle 
          cx={size/2} cy={size/2} r={45} 
          stroke={color} strokeWidth="10" fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color }}>{grade}</span>
        <span className="text-xs text-gray-500">{score}/100</span>
      </div>
    </div>
  );
};

// Mini Sparkline
const Sparkline = ({ data, color = '#C41E3A', height = 30 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data}>
      <Area type="monotone" dataKey="value" stroke={color} fill={color} fillOpacity={0.2} strokeWidth={1.5} />
    </AreaChart>
  </ResponsiveContainer>
);

// Metric Card with Sparkline
const MetricCard = ({ title, value, subtitle, trend, trendValue, sparkData, icon: Icon, color = 'gray' }) => {
  const colorClasses = {
    green: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', border: 'border-red-200' },
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200' },
    gray: { bg: 'bg-gray-50', icon: 'text-gray-600', border: 'border-gray-200' }
  };
  const c = colorClasses[color];
  
  return (
    <div className={`${c.bg} rounded-xl p-4 border ${c.border}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${c.icon}`} />}
          <span className="text-gray-600 text-xs font-medium">{title}</span>
        </div>
        {trend && (
          <div className={`flex items-center gap-0.5 text-xs ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'}`}>
            {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : trend === 'down' ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <p className="text-xl font-bold text-gray-800">{value}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      {sparkData && sparkData.length > 1 && (
        <div className="mt-2">
          <Sparkline data={sparkData} color={c.icon.replace('text-', '#').replace('-600', '')} />
        </div>
      )}
    </div>
  );
};

// Status Indicator
const StatusBadge = ({ status, label }) => {
  const styles = {
    excellent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    good: 'bg-blue-100 text-blue-700 border-blue-200',
    warning: 'bg-amber-100 text-amber-700 border-amber-200',
    critical: 'bg-red-100 text-red-700 border-red-200'
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status]}`}>
      {label}
    </span>
  );
};

// KPI Row Component
const KPIRow = ({ label, value, benchmark, status, icon: Icon }) => {
  const statusColors = {
    excellent: 'text-emerald-600',
    good: 'text-blue-600',
    warning: 'text-amber-600',
    critical: 'text-red-600'
  };
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <span className="text-gray-600 text-sm">{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-400">{benchmark}</span>
        <span className={`font-bold ${statusColors[status]}`}>{value}</span>
      </div>
    </div>
  );
};

// Cash Flow Forecast Component
const CashFlowForecast = ({ currentCash, monthlyBurn, monthlyIncome }) => {
  const months = [];
  let balance = currentCash;
  const netFlow = monthlyIncome - monthlyBurn;
  
  for (let i = 0; i <= 12; i++) {
    months.push({
      month: i === 0 ? 'Now' : `M+${i}`,
      balance: Math.max(0, balance),
      projected: balance
    });
    balance += netFlow;
  }
  
  const runwayMonth = months.findIndex(m => m.balance <= 0);
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs text-gray-500">Monthly Net Flow</p>
          <p className={`text-lg font-bold ${netFlow >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatCurrency(netFlow, 'RM', true)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">Projected Runway</p>
          <p className={`text-lg font-bold ${runwayMonth === -1 ? 'text-emerald-600' : runwayMonth <= 6 ? 'text-red-600' : 'text-amber-600'}`}>
            {runwayMonth === -1 ? '12+ bulan' : `${runwayMonth} bulan`}
          </p>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <AreaChart data={months}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 9 }} />
          <YAxis tick={{ fill: '#6b7280', fontSize: 9 }} tickFormatter={formatCompact} />
          <Tooltip formatter={(value) => formatCurrency(value, 'RM')} />
          <Area type="monotone" dataKey="balance" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// AI Executive Summary Generator
const generateExecutiveSummary = (metrics) => {
  const { healthScore, profitMargin, cashRunway, revenueGrowth, totalRevenue, totalExpenses, netProfit, totalStaff } = metrics;
  const grade = getHealthGrade(healthScore);
  
  let summary = `**Status Kesihatan Kewangan: ${grade.grade} (${grade.label})**\n\n`;
  
  if (healthScore >= 80) {
    summary += `Syarikat berada dalam keadaan kewangan yang kukuh. `;
  } else if (healthScore >= 60) {
    summary += `Syarikat dalam keadaan sederhana dengan beberapa aspek yang perlu diperbaiki. `;
  } else {
    summary += `⚠️ PERHATIAN: Syarikat memerlukan tindakan segera untuk memperbaiki kesihatan kewangan. `;
  }
  
  // Revenue analysis
  if (totalRevenue > 0) {
    summary += `Jumlah revenue adalah ${formatCurrency(totalRevenue, '$')} dengan profit margin ${profitMargin.toFixed(1)}%. `;
  }
  
  // Cash runway
  if (cashRunway < 3) {
    summary += `🚨 Cash runway hanya ${cashRunway.toFixed(1)} bulan - kritikal! `;
  } else if (cashRunway < 6) {
    summary += `Cash runway ${cashRunway.toFixed(1)} bulan perlu perhatian. `;
  }
  
  // Staff efficiency
  if (totalStaff > 0 && totalRevenue > 0) {
    const revenuePerStaff = totalRevenue / totalStaff;
    if (revenuePerStaff < 3000) {
      summary += `Revenue per employee (${formatCurrency(revenuePerStaff, '$')}) di bawah benchmark industri. `;
    }
  }
  
  return summary;
};

const ExecutiveDashboard = () => {
  const { 
    revenueList, expenseList, cashflowList, staffList, targetBudgetList,
    loading 
  } = useApp();

  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [assetData, setAssetData] = useState(null);
  const [hrData, setHrData] = useState(null);
  const [extraLoading, setExtraLoading] = useState(true);

  useEffect(() => {
    const fetchExtra = async () => {
      try {
        const [assetRes, hrRes] = await Promise.all([
          axios.get(`${API}/assets/summary/dashboard`),
          axios.get(`${API}/hr/dashboard`)
        ]);
        setAssetData(assetRes.data);
        setHrData(hrRes.data);
      } catch (e) { console.error(e); }
      finally { setExtraLoading(false); }
    };
    fetchExtra();
  }, []);

  // Get date range based on selected period
  const dateRange = useMemo(() => getDateRange(selectedPeriod), [selectedPeriod]);

  if (loading || extraLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  // Safe arrays
  const safeRevenueAll = Array.isArray(revenueList) ? revenueList : [];
  const safeExpensesAll = Array.isArray(expenseList) ? expenseList : [];
  const safeCashflow = Array.isArray(cashflowList) ? cashflowList : [];
  const safeStaff = Array.isArray(staffList) ? staffList : [];

  // Filter data by selected period
  const safeRevenue = filterByDateRange(safeRevenueAll, dateRange);
  const safeExpenses = filterByDateRange(safeExpensesAll, dateRange);

  // Period label for display
  const periodLabel = PERIOD_OPTIONS.find(p => p.id === selectedPeriod)?.label || 'Semua';

  // ============ CALCULATIONS ============
  
  // Basic Totals (convert USD to MYR for consistency)
  const totalRevenueUSD = safeRevenue.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalExpensesUSD = safeExpenses.reduce((sum, item) => sum + (item.amount || 0), 0);
  const netProfitUSD = totalRevenueUSD - totalExpensesUSD;
  
  const totalRevenueMYR = totalRevenueUSD * USD_TO_MYR;
  const totalExpensesMYR = totalExpensesUSD * USD_TO_MYR;
  const netProfitMYR = netProfitUSD * USD_TO_MYR;
  
  const latestCashBalance = safeCashflow.length > 0 ? (safeCashflow[0].closing_balance || 0) : 0;
  
  // Staff Metrics
  const totalStaff = safeStaff.length;
  const activeStaff = safeStaff.filter(s => s.status === 'Active').length;
  const totalGrossSalary = safeStaff.reduce((sum, s) => sum + (s.gross_salary || 0), 0);
  const totalNetPay = safeStaff.reduce((sum, s) => sum + (s.net_pay || 0), 0);
  
  // ============ FINANCIAL RATIOS ============
  
  const profitMargin = totalRevenueUSD > 0 ? (netProfitUSD / totalRevenueUSD) * 100 : 0;
  const expenseRatio = totalRevenueUSD > 0 ? (totalExpensesUSD / totalRevenueUSD) * 100 : 0;
  const revenuePerEmployee = activeStaff > 0 ? totalRevenueUSD / activeStaff : 0;
  const salaryToRevenueRatio = totalRevenueMYR > 0 ? (totalGrossSalary / totalRevenueMYR) * 100 : 0;
  
  // Cash Runway (using MYR)
  const monthlyBurnRate = totalGrossSalary + (totalExpensesMYR / 12);
  const cashRunway = monthlyBurnRate > 0 ? latestCashBalance / monthlyBurnRate : 12;
  
  // Break-even
  const fixedCosts = totalGrossSalary;
  const variableCostsUSD = safeExpenses.filter(e => e.expense_category !== 'Salary').reduce((sum, e) => sum + (e.amount || 0), 0);
  const contributionMargin = totalRevenueUSD > 0 ? ((totalRevenueUSD - variableCostsUSD) / totalRevenueUSD) * 100 : 0;
  const breakEvenRevenue = contributionMargin > 0 ? (fixedCosts / (contributionMargin / 100)) : 0;
  const marginOfSafety = totalRevenueMYR > 0 ? ((totalRevenueMYR - breakEvenRevenue) / totalRevenueMYR) * 100 : 0;
  
  // Month-over-Month
  const currentMonth = new Date().toISOString().slice(0, 7);
  const lastMonth = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 7);
  
  const currentMonthRevenue = safeRevenue.filter(r => r.date?.startsWith(currentMonth)).reduce((sum, r) => sum + (r.amount || 0), 0);
  const lastMonthRevenue = safeRevenue.filter(r => r.date?.startsWith(lastMonth)).reduce((sum, r) => sum + (r.amount || 0), 0);
  const revenueGrowth = lastMonthRevenue > 0 ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 : 0;
  
  // Health Score
  const healthScore = calculateHealthScore({
    profitMargin,
    cashRunway,
    expenseRatio,
    revenueGrowth,
    marginOfSafety
  });
  const healthGrade = getHealthGrade(healthScore);
  
  // Monthly Trend Data
  const monthlyData = {};
  safeRevenue.forEach(r => {
    const month = r.date?.slice(0, 7);
    if (month) {
      if (!monthlyData[month]) monthlyData[month] = { month, revenue: 0, expenses: 0, profit: 0 };
      monthlyData[month].revenue += r.amount || 0;
    }
  });
  safeExpenses.forEach(e => {
    const month = e.date?.slice(0, 7);
    if (month) {
      if (!monthlyData[month]) monthlyData[month] = { month, revenue: 0, expenses: 0, profit: 0 };
      monthlyData[month].expenses += e.amount || 0;
    }
  });
  Object.values(monthlyData).forEach(m => { m.profit = m.revenue - m.expenses; });
  const trendData = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  
  // Sparkline data
  const revenueSparkData = trendData.map(d => ({ value: d.revenue }));
  const expenseSparkData = trendData.map(d => ({ value: d.expenses }));
  const profitSparkData = trendData.map(d => ({ value: d.profit }));
  
  // Expense breakdown
  const expenseByCategory = Object.entries(safeExpenses.reduce((acc, e) => {
    const cat = e.expense_category || 'Other';
    acc[cat] = (acc[cat] || 0) + (e.amount || 0);
    return acc;
  }, {})).map(([category, amount]) => ({ category, amount, percentage: totalExpensesUSD > 0 ? (amount / totalExpensesUSD) * 100 : 0 })).sort((a, b) => b.amount - a.amount);

  // Executive Summary
  const executiveSummary = generateExecutiveSummary({
    healthScore, profitMargin, cashRunway, revenueGrowth, 
    totalRevenue: totalRevenueUSD, totalExpenses: totalExpensesUSD, netProfit: netProfitUSD, totalStaff
  });

  // Risk Assessment
  const risks = [];
  if (cashRunway < 3) risks.push({ level: 'critical', text: 'Cash runway kritikal (<3 bulan)' });
  if (cashRunway < 6 && cashRunway >= 3) risks.push({ level: 'warning', text: 'Cash runway rendah (3-6 bulan)' });
  if (profitMargin < 10) risks.push({ level: 'warning', text: 'Profit margin rendah (<10%)' });
  if (salaryToRevenueRatio > 50) risks.push({ level: 'warning', text: 'Kos gaji tinggi (>50% revenue)' });
  if (revenueGrowth < -10) risks.push({ level: 'critical', text: 'Revenue menurun >10%' });

  // Opportunities
  const opportunities = [];
  if (profitMargin > 30) opportunities.push('Profit margin tinggi - boleh expand');
  if (cashRunway > 12) opportunities.push('Cash reserve kukuh - boleh invest');
  if (revenueGrowth > 20) opportunities.push('Growth momentum baik - leverage');

  return (
    <div className="space-y-6">
      {/* Header with Health Score */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-8 h-8 text-red-400" />
              <div>
                <h1 className="text-2xl font-bold">Executive Dashboard</h1>
                <p className="text-gray-400 text-sm">CFO & Director Decision Support System</p>
              </div>
            </div>
            <div className="mt-4 p-4 bg-white/5 rounded-xl backdrop-blur">
              <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{executiveSummary}</p>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <p className="text-gray-400 text-xs mb-2">FINANCIAL HEALTH SCORE</p>
            <ScoreRing score={healthScore} size={130} />
            <p className="text-sm mt-2 font-medium" style={{ color: healthGrade.color }}>{healthGrade.label}</p>
          </div>
        </div>
      </div>

      {/* Period Filter */}
      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-red-700" />
            <span className="font-medium text-gray-800">Tempoh Analisis:</span>
            <span className="text-sm text-gray-500">
              {dateRange.start ? `${dateRange.start} → ${dateRange.end}` : 'Semua data'}
            </span>
          </div>
          <PeriodSelector selected={selectedPeriod} onChange={setSelectedPeriod} />
        </div>
        {selectedPeriod !== 'all' && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 text-sm">
            <span className="text-gray-500">Data untuk <strong className="text-red-700">{periodLabel}</strong>:</span>
            <span className="text-emerald-600">{safeRevenue.length} revenue</span>
            <span className="text-red-600">{safeExpenses.length} expenses</span>
          </div>
        )}
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={`Revenue (${periodLabel})`}
          value={formatCurrency(totalRevenueUSD, '$')}
          subtitle={`≈ ${formatCurrency(totalRevenueMYR, 'RM')}`}
          trend={revenueGrowth > 0 ? 'up' : revenueGrowth < 0 ? 'down' : 'neutral'}
          trendValue={formatPercent(revenueGrowth)}
          sparkData={revenueSparkData}
          icon={TrendingUp}
          color="green"
        />
        <MetricCard
          title={`Expenses (${periodLabel})`}
          value={formatCurrency(totalExpensesUSD, '$')}
          subtitle={`≈ ${formatCurrency(totalExpensesMYR, 'RM')}`}
          sparkData={expenseSparkData}
          icon={TrendingDown}
          color="red"
        />
        <MetricCard
          title={`Net Profit (${periodLabel})`}
          value={formatCurrency(netProfitUSD, '$')}
          subtitle={profitMargin >= 0 ? `Margin: ${profitMargin.toFixed(1)}%` : 'Loss'}
          sparkData={profitSparkData}
          icon={DollarSign}
          color={netProfitUSD >= 0 ? 'green' : 'red'}
        />
        <MetricCard
          title="Cash Balance"
          value={formatCurrency(latestCashBalance, '$')}
          subtitle={`Runway: ${cashRunway.toFixed(1)} bulan`}
          icon={Activity}
          color={cashRunway >= 6 ? 'blue' : cashRunway >= 3 ? 'amber' : 'red'}
        />
      </div>

      {/* Financial Ratios with Gauges */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-gray-800 font-semibold flex items-center gap-2">
            <Gauge className="w-5 h-5 text-red-700" /> Financial Ratios & KPIs
          </h3>
          <div className="flex gap-2">
            <StatusBadge status={healthScore >= 70 ? 'good' : healthScore >= 50 ? 'warning' : 'critical'} label={`Score: ${healthScore}`} />
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <GaugeChart value={profitMargin} max={100} label="Profit Margin" thresholds={{ good: 20, warning: 10 }} />
            <StatusBadge status={profitMargin >= 20 ? 'excellent' : profitMargin >= 10 ? 'good' : profitMargin >= 0 ? 'warning' : 'critical'} label={profitMargin >= 20 ? 'Excellent' : profitMargin >= 10 ? 'Good' : 'Low'} />
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <GaugeChart value={100 - expenseRatio} max={100} label="Efficiency" thresholds={{ good: 40, warning: 20 }} />
            <StatusBadge status={expenseRatio <= 60 ? 'excellent' : expenseRatio <= 80 ? 'warning' : 'critical'} label={`Expense: ${expenseRatio.toFixed(0)}%`} />
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <GaugeChart value={Math.min(cashRunway, 12)} max={12} label="Cash Runway" thresholds={{ good: 6, warning: 3 }} unit=" bln" />
            <StatusBadge status={cashRunway >= 6 ? 'good' : cashRunway >= 3 ? 'warning' : 'critical'} label={cashRunway >= 6 ? 'Safe' : cashRunway >= 3 ? 'Monitor' : 'Critical'} />
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-xl">
            <GaugeChart value={Math.max(0, marginOfSafety)} max={50} label="Safety Margin" thresholds={{ good: 30, warning: 15 }} />
            <StatusBadge status={marginOfSafety >= 30 ? 'excellent' : marginOfSafety >= 15 ? 'good' : marginOfSafety >= 0 ? 'warning' : 'critical'} label={marginOfSafety >= 0 ? 'Above BE' : 'Below BE'} />
          </div>
        </div>
      </div>

      {/* Risk & Opportunities */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Risks */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-semibold flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-red-700" /> Risk Assessment
          </h3>
          {risks.length > 0 ? (
            <div className="space-y-2">
              {risks.map((risk, idx) => (
                <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg ${risk.level === 'critical' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <AlertCircle className={`w-5 h-5 ${risk.level === 'critical' ? 'text-red-600' : 'text-amber-600'}`} />
                  <span className="text-sm text-gray-700">{risk.text}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <span className="text-sm text-emerald-700">Tiada risiko kritikal dikesan</span>
            </div>
          )}
        </div>

        {/* Opportunities */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-semibold flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5 text-amber-500" /> Opportunities
          </h3>
          {opportunities.length > 0 ? (
            <div className="space-y-2">
              {opportunities.map((opp, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <ThumbsUp className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-gray-700">{opp}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Info className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">Tambah lebih banyak data untuk analisis peluang</span>
            </div>
          )}
        </div>
      </div>

      {/* Cash Flow Forecast */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="text-gray-800 font-semibold flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-red-700" /> Cash Flow Forecast (12 Bulan)
        </h3>
        <CashFlowForecast 
          currentCash={latestCashBalance} 
          monthlyBurn={monthlyBurnRate}
          monthlyIncome={totalRevenueMYR / 12}
        />
      </div>

      {/* Trend Chart */}
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
        <h3 className="text-gray-800 font-semibold flex items-center gap-2 mb-4">
          <Growth className="w-5 h-5 text-red-700" /> Revenue, Expenses & Profit Trend
        </h3>
        {trendData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} />
              <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={formatCompact} />
              <Tooltip formatter={(value) => formatCurrency(value, '$')} contentStyle={{ borderRadius: '12px', border: '1px solid #e5e7eb' }} />
              <Legend />
              <Bar dataKey="revenue" fill="#10B981" name="Revenue" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" fill="#EF4444" name="Expenses" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="profit" stroke="#3B82F6" strokeWidth={3} name="Profit" dot={{ fill: '#3B82F6', strokeWidth: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-gray-400">No trend data available</div>
        )}
      </div>

      {/* Staff & Expense Analysis */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Staff Metrics */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-semibold flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-red-700" /> Staff & Productivity
          </h3>
          <div className="space-y-1">
            <KPIRow label="Total Staff" value={`${totalStaff} orang`} benchmark={`${activeStaff} active`} status="good" icon={Users} />
            <KPIRow label="Revenue/Employee" value={formatCurrency(revenuePerEmployee, '$')} benchmark="Target: $5K+" status={revenuePerEmployee >= 5000 ? 'excellent' : revenuePerEmployee >= 3000 ? 'good' : 'warning'} icon={DollarSign} />
            <KPIRow label="Cost/Employee" value={formatCurrency(activeStaff > 0 ? totalGrossSalary / activeStaff : 0, 'RM')} benchmark="Avg salary" status="good" icon={Calculator} />
            <KPIRow label="Total Payroll" value={formatCurrency(totalGrossSalary, 'RM')} benchmark="Monthly" status="good" icon={FileText} />
            <KPIRow label="Salary/Revenue" value={formatPercent(salaryToRevenueRatio, false)} benchmark="<30%" status={salaryToRevenueRatio <= 30 ? 'excellent' : salaryToRevenueRatio <= 50 ? 'warning' : 'critical'} icon={Target} />
          </div>
        </div>

        {/* Expense Distribution */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-semibold flex items-center gap-2 mb-4">
            <PieChart className="w-5 h-5 text-red-700" /> Expense Distribution
          </h3>
          {expenseByCategory.length > 0 ? (
            <div className="flex items-center gap-6">
              <div className="w-1/2">
                <ResponsiveContainer width="100%" height={180}>
                  <RechartsPie>
                    <Pie data={expenseByCategory} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="amount" nameKey="category">
                      {expenseByCategory.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value, '$')} />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-2">
                {expenseByCategory.map((item, idx) => (
                  <div key={item.category} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                      <span className="text-sm text-gray-600">{item.category}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-800">{item.percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[180px] flex items-center justify-center text-gray-400">No expense data</div>
          )}
        </div>
      </div>

      {/* Quick Actions Summary */}
      <div className="bg-gradient-to-r from-red-700 to-red-800 rounded-2xl p-6">
        <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
          <Brain className="w-5 h-5" /> AI Action Items
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-white/60 text-xs mb-1">Immediate Action</p>
            <p className="text-white text-sm font-medium">
              {cashRunway < 6 ? '🚨 Tingkatkan cash reserve segera' : 
               profitMargin < 10 ? '📊 Review pricing strategy' :
               '✅ Monitor dan maintain performance'}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-white/60 text-xs mb-1">Short-term Focus</p>
            <p className="text-white text-sm font-medium">
              {expenseRatio > 70 ? '💰 Optimize operational costs' :
               revenueGrowth < 0 ? '📈 Boost sales & marketing' :
               '🎯 Maintain growth momentum'}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-white/60 text-xs mb-1">Strategic Priority</p>
            <p className="text-white text-sm font-medium">
              {revenuePerEmployee < 3000 ? '👥 Improve team productivity' :
               salaryToRevenueRatio > 50 ? '🏢 Review organization structure' :
               '🚀 Plan for expansion'}
            </p>
          </div>
        </div>
      </div>

      {/* HR & Asset Summary Section */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* HR Summary */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-semibold flex items-center gap-2 mb-4">
            <UserCog className="w-5 h-5 text-red-700" /> HR Overview
          </h3>
          <div className="space-y-1">
            <KPIRow label="Staf Aktif" value={`${hrData?.summary?.total_staff || 0} orang`} benchmark={`${hrData?.summary?.total_evaluated || 0} dinilai`} status="good" icon={Users} />
            <KPIRow label="Perlu Latihan" value={`${hrData?.summary?.staff_need_training || 0}`} benchmark="flagged" status={hrData?.summary?.staff_need_training > 3 ? 'warning' : 'good'} icon={BookOpen} />
            <KPIRow label="Prestasi Rendah" value={`${hrData?.summary?.staff_low_performance || 0}`} benchmark="need attention" status={hrData?.summary?.staff_low_performance > 0 ? 'critical' : 'excellent'} icon={TrendingDown} />
            <KPIRow label="Amaran Aktif" value={`${hrData?.summary?.active_warnings || 0}`} benchmark="disciplinary" status={hrData?.summary?.active_warnings > 0 ? 'critical' : 'excellent'} icon={AlertOctagon} />
            <KPIRow label="Cuti Pending" value={`${hrData?.summary?.pending_leaves || 0}`} benchmark="await approval" status={hrData?.summary?.pending_leaves > 5 ? 'warning' : 'good'} icon={Calendar} />
            <KPIRow label="Turnover YTD" value={`${hrData?.summary?.turnover_ytd || 0}`} benchmark="exits this year" status={hrData?.summary?.turnover_ytd > 5 ? 'warning' : 'good'} icon={Users} />
          </div>
          {/* Attendance Today */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">Kehadiran Hari Ini</p>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-emerald-50 p-2 rounded"><p className="text-lg font-bold text-emerald-600">{hrData?.attendance_today?.hadir || 0}</p><p className="text-xs text-gray-500">Hadir</p></div>
              <div className="bg-blue-50 p-2 rounded"><p className="text-lg font-bold text-blue-600">{hrData?.attendance_today?.cuti || 0}</p><p className="text-xs text-gray-500">Cuti</p></div>
              <div className="bg-amber-50 p-2 rounded"><p className="text-lg font-bold text-amber-600">{hrData?.attendance_today?.mc || 0}</p><p className="text-xs text-gray-500">MC</p></div>
              <div className="bg-red-50 p-2 rounded"><p className="text-lg font-bold text-red-600">{(hrData?.attendance_today?.lewat || 0) + (hrData?.attendance_today?.tidak_hadir || 0)}</p><p className="text-xs text-gray-500">Isu</p></div>
            </div>
          </div>
        </div>

        {/* Asset Summary */}
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
          <h3 className="text-gray-800 font-semibold flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-red-700" /> Asset Overview
          </h3>
          <div className="space-y-1">
            <KPIRow label="Jumlah Aset" value={`${assetData?.total_assets || 0}`} benchmark={`${assetData?.by_category?.length || 0} kategori`} status="good" icon={Package} />
            <KPIRow label="Nilai Belian" value={formatCurrency((assetData?.total_purchase_value || 0), 'RM').replace('$', '')} benchmark="original cost" status="good" icon={DollarSign} />
            <KPIRow label="Nilai Semasa" value={formatCurrency((assetData?.total_current_value || 0), 'RM').replace('$', '')} benchmark="after depreciation" status="good" icon={TrendingDown} />
            <KPIRow label="Susut Nilai" value={formatCurrency((assetData?.total_depreciation || 0), 'RM').replace('$', '')} benchmark="accumulated" status="warning" icon={TrendingDown} />
            <KPIRow label="Waranti Tamat" value={`${assetData?.alerts?.warranty_expiring_soon?.length || 0}`} benchmark="within 90 days" status={assetData?.alerts?.warranty_expiring_soon?.length > 0 ? 'warning' : 'excellent'} icon={Shield} />
            <KPIRow label="Maintenance Due" value={`${(assetData?.alerts?.maintenance_overdue?.length || 0) + (assetData?.alerts?.maintenance_due_soon?.length || 0)}`} benchmark="needs attention" status={(assetData?.alerts?.maintenance_overdue?.length || 0) > 0 ? 'critical' : 'good'} icon={Activity} />
          </div>
          {/* Asset Distribution */}
          {assetData?.by_category?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Aset Mengikut Kategori</p>
              <div className="space-y-1">
                {assetData.by_category.slice(0, 4).map((cat, idx) => (
                  <div key={cat.category} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">{cat.category}</span>
                    <span className="font-medium">{cat.count} <span className="text-gray-400 text-xs">(RM{(cat.current_value || 0).toLocaleString()})</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer with timestamp */}
      <div className="flex items-center justify-between text-xs text-gray-400 px-2">
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3" />
          <span>Last updated: {new Date().toLocaleString('ms-MY')}</span>
        </div>
        <span>Exchange Rate: 1 USD = {USD_TO_MYR} MYR</span>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
